// The office's invoice sheet (rs-office1's walk): one finalized invoice in words, read from
// invoices.get. Its status as the office says it, when it's due, and what settled it.

import { dateText } from '@flowology/kernel';
import type { CardCheck } from '../history/history-model.ts';
import { money } from '../review/review-model.ts';
import type { AcceptanceSignature } from '../shared/signed-model.ts';

export interface Applied {
  kind: 'payment' | 'credit' | 'write_off';
  at: string;
  amountCents: number;
  paymentId: string | null;
  method: string | null;
  /** A payment's whole amount and what of it was kept as the customer's credit (QA-716). */
  payment?: { amountCents: number; keptAsCreditCents: number } | null;
  /**
   * A credit that is a credit note, not money (history fix 2): the invoice it's on, ServiceTitan's
   * number for an adjustment invoice that came over as one, and its day.
   */
  creditNote?: { invoiceNumber: string; number: string | null; issuedOn: string } | null;
}

export interface InvoiceSheetData {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  jobId: string | null;
  jobNumber: string | null;
  poNumber: string | null;
  issuedOn: string | null;
  dueOn: string | null;
  status: 'draft' | 'finalized' | 'sent' | 'partially_paid' | 'paid' | 'void';
  subtotalCents: number;
  taxCents: number;
  /** "Tax exempt: …" when its bill-to was exempt as it was drafted (BL-094), else null. */
  taxExempt?: string | null;
  totalCents: number;
  balanceCents: number;
  writtenOffCents: number;
  /** What credit notes took off it (history fix 2): settled, never paid. */
  adjustedCents?: number;
  /** What was given back on it (QA-713): refunds, each a credit note, and the tax they took off. */
  refunded?: { cents: number; taxCents: number; taxes: { code: string; taxCents: number }[] };
  /** It came over from ServiceTitan: ServiceTitan's while the company mirrors it. */
  imported?: boolean;
  lines: {
    id: string;
    description: string;
    quantity: string;
    unitPriceCents: number;
    totalCents: number;
    overrideReason: string | null;
    fromEstimate: boolean;
    /** A line from ServiceTitan: its item's code and its technician (history fix 2). */
    code?: string | null;
    technician?: string | null;
  }[];
  taxes: { code: string; rate: string; baseCents: number; taxCents: number }[];
  applied: Applied[];
  /** A card charge on it still being checked: nothing else is taken on it until it is (QA-307). */
  cardBeingChecked?: CardCheck | null;
  /** The customer's signature accepting the work (invoices.get, QA-727); null unsigned. */
  signature?: AcceptanceSignature | null;
}

const dayCount = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);

/** The status as the office says it: overdue and written off are read from the money, not stored. */
export function statusOf(
  inv: InvoiceSheetData,
  today: string,
): { word: string; tone: 'ok' | 'bad' | 'warn' | '' } {
  if (inv.status === 'void') return { word: 'Void', tone: '' };
  // a draft hasn't gone to the customer: never Sent or Overdue, as the job page says it (QA-804)
  if (inv.status === 'draft')
    return inv.totalCents > 0 && inv.balanceCents <= 0
      ? { word: 'Draft, paid', tone: 'ok' }
      : { word: 'Draft', tone: '' };
  if (inv.balanceCents <= 0)
    return inv.writtenOffCents > 0 && inv.writtenOffCents >= inv.totalCents
      ? { word: 'Written off', tone: '' }
      : { word: 'Paid', tone: 'ok' };
  if (inv.dueOn && inv.dueOn < today) return { word: 'Overdue', tone: 'bad' };
  if (inv.status === 'partially_paid') return { word: 'Partly paid', tone: 'warn' };
  return { word: inv.status === 'finalized' ? 'Finalized' : 'Sent', tone: '' };
}

/** Sent on window when money moves in the invoice dialog (a payment, a write-off…), naming the customer. */
export const INVOICE_MOVED = 'flw:invoice-moved';

/** The dialog moved money on this customer's invoice: their pages under it read again (QA-805). */
export function invoiceMoved(on: EventTarget, customerId: string): void {
  on.dispatchEvent(new CustomEvent(INVOICE_MOVED, { detail: { customerId } }));
}

/** Calls `then` whenever the dialog moves money on this customer's invoices; returns the way to stop. */
export function onInvoiceMoved(on: EventTarget, customerId: string, then: () => void): () => void {
  const heard = (e: Event) => {
    if ((e as CustomEvent<{ customerId: string }>).detail?.customerId === customerId) then();
  };
  on.addEventListener(INVOICE_MOVED, heard);
  return () => on.removeEventListener(INVOICE_MOVED, heard);
}

/**
 * What a draft waits for (QA-804): it can't be sent or written off until it is finalised, which
 * completing its job does.
 */
export function draftNote(inv: Pick<InvoiceSheetData, 'status' | 'jobId'>): string | null {
  if (inv.status !== 'draft') return null;
  return inv.jobId
    ? 'A draft: it’s finalised when the job is completed, and can be sent to the customer then.'
    : 'A draft: it can be sent to the customer once it’s finalised.';
}

/** "Due Oct 17 · in 3 days", "Due Oct 17 · 4 days overdue", "Due today"; nothing once settled. */
export function dueText(inv: Pick<InvoiceSheetData, 'dueOn' | 'balanceCents'>, today: string): string | null {
  if (!inv.dueOn) return null;
  const when = `Due ${dateText(inv.dueOn, 'UTC', { year: false, now: today })}`;
  if (inv.balanceCents <= 0) return when;
  const n = dayCount(today, inv.dueOn);
  if (n === 0) return `${when} · today`;
  return n > 0
    ? `${when} · in ${n} day${n === 1 ? '' : 's'}`
    : `${when} · ${-n} day${n === -1 ? '' : 's'} overdue`;
}

/**
 * What was paid on it: the total less what's still owed, what was written off, and what credit notes
 * took off it (an adjustment is never money in, history fix 2).
 */
export const paidOf = (
  inv: Pick<InvoiceSheetData, 'totalCents' | 'balanceCents' | 'writtenOffCents' | 'adjustedCents'>,
) => inv.totalCents - inv.balanceCents - inv.writtenOffCents - (inv.adjustedCents ?? 0);

/**
 * The invoice after what was given back on it (QA-713). A refund is a credit note against it: the
 * invoice stays paid, what it bills and each tax go down by the credit note (as the books post it),
 * and what was paid goes down by the money given back. With no refund, the invoice as it is.
 */
export function afterRefunds(
  inv: Pick<
    InvoiceSheetData,
    'totalCents' | 'balanceCents' | 'writtenOffCents' | 'adjustedCents' | 'refunded' | 'taxes'
  >,
): {
  refundedCents: number;
  totalCents: number;
  paidCents: number;
  taxes: { code: string; taxCents: number }[];
} {
  const r = inv.refunded;
  const off = (code: string) => r?.taxes.find((t) => t.code === code)?.taxCents ?? 0;
  return {
    refundedCents: r?.cents ?? 0,
    totalCents: inv.totalCents - (r?.cents ?? 0),
    paidCents: paidOf(inv) - (r?.cents ?? 0),
    taxes: inv.taxes.map((t) => ({ code: t.code, taxCents: t.taxCents - off(t.code) })),
  };
}

/** "GST $2.37, PST $0.27": what each tax went down by on its refunds; null when none did. */
export function refundTaxText(inv: Pick<InvoiceSheetData, 'refunded'>): string | null {
  const taxes = (inv.refunded?.taxes ?? []).filter((t) => t.taxCents !== 0);
  return taxes.length ? taxes.map((t) => `${t.code} ${money(t.taxCents)}`).join(', ') : null;
}

/**
 * Where the rest of a payment went when it was more than this invoice took (QA-716): "of $500.00;
 * $319.44 kept as credit", or "of $500.00" when the rest paid other invoices; null when it all went here.
 */
export function restOfPayment(a: Applied): string | null {
  const p = a.kind === 'payment' ? a.payment : null;
  if (!p || p.amountCents <= a.amountCents) return null;
  return p.keptAsCreditCents > 0
    ? `of ${money(p.amountCents)}; ${money(p.keptAsCreditCents)} kept as credit`
    : `of ${money(p.amountCents)}`;
}

const METHOD: Record<string, string> = {
  card: 'card',
  etransfer: 'e-transfer',
  cheque: 'cheque',
  cash: 'cash',
  eft: 'EFT',
  financing: 'financing',
  other: 'another way',
};

/**
 * A credit note on this invoice that took money off it (history fix 2): "Adjusted by #48364901 · Oct 3"
 * for ServiceTitan's adjustment invoice, "Adjusted by a credit note · Oct 3" for one of ours; null for
 * anything else.
 */
export function adjustedWord(a: Applied, invoiceNumber: string, day: (d: string) => string): string | null {
  const n = a.kind === 'credit' ? a.creditNote : null;
  if (!n || n.invoiceNumber !== invoiceNumber) return null;
  return `Adjusted by ${n.number ? `#${n.number}` : 'a credit note'} · ${day(n.issuedOn)}`;
}

/** "Payment by cheque", "Payment", "Credit used", "Credit note from invoice 1042", "Written off". */
export function appliedWord(a: Applied): string {
  if (a.kind === 'credit')
    return a.creditNote ? `Credit note from invoice ${a.creditNote.invoiceNumber}` : 'Credit used';
  if (a.kind === 'write_off') return 'Written off';
  const how = a.method ? ` by ${METHOD[a.method] ?? a.method.replace(/_/g, ' ')}` : '';
  // ServiceTitan's refund, imported as a negative share of the invoice (#66): a refund, never a payment
  if (a.amountCents < 0) return `Refunded${how} in ServiceTitan`;
  return a.method ? `Payment${how}` : 'Payment';
}

/** "1.000" → "1", "1.500" → "1.5": a quantity as the office says it. */
export const qtyText = (q: string) => (q.includes('.') ? q.replace(/\.?0+$/, '') : q);

/** "GST 5% on $120.00": a tax line's rate as a percent. */
export function rateText(rate: string): string {
  const pct = Number(rate) * 100;
  return `${Number.isFinite(pct) ? Number(pct.toFixed(3)) : rate}%`;
}
